import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import linkedin from "../../images/linkedin-logo.png";
import cv from "../../images/cv-icon.png";
import github from "../../images/github.png";
import jumpstart from "../../images/jumpstart_logo.jpeg";
import { differenceInDays } from "date-fns";
import Office from "../../images/office-icon.png"; // Ensure you have the loading.svg in the correct path
import Languages from "../../images/languages-icon.png"; // Ensure you have the loading.svg in the correct path
import LoadingBlack from "../../images/loading-black.svg"; // Ensure you have the loading.svg in the correct path
import Loading from "../../images/loading-black.svg"; // For application buttons loading
import axios from "axios";
import { useShortlisted } from "../../provider/ShortlistedContext";
import VideoIntro from "../../images/video-icon-green.png";
import { useAsyncError, useNavigate } from "react-router-dom";
import MatchRequestModal from "../modals/MatchRequestModal"; // Original modal (keeping for reference)
import RoleMatchRequestModal from "../modals/RoleMatchRequestModal"; // New streamlined modal for role selection
import StarIcon from '@mui/icons-material/Star'; // Import Star icon
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // Import dropdown arrow icon

const languageLogos = {
  JavaScript: "/images/coding_logo/javascript.png",
  Python: "/images/coding_logo/python.png",
  C: "/images/coding_logo/c.png",
  Swift: "/images/coding_logo/swift.png",
  "C#": "/images/coding_logo/csharp.png",
  "C++": "/images/coding_logo/cplusplus.png", // For C++
  Java: "/images/coding_logo/java.svg",
  Go: "/images/coding_logo/go.png",
  PHP: "/images/coding_logo/php.svg",
  Ruby: "/images/coding_logo/ruby.webp",
  Kotlin: "/images/coding_logo/kotlin.png",
  Rust: "/images/coding_logo/rust.png",

  // Newly added languages
  Clojure: "/images/coding_logo/clojure.png",
  Haskell: "/images/coding_logo/haskell.png",
  Dart: "/images/coding_logo/dart.png",
  Elixir: "/images/coding_logo/elixir.webp",
  "Objective-C": "/images/coding_logo/objc.svg",
  Perl: "/images/coding_logo/perl.png",
  Scala: "/images/coding_logo/scala.png",
  Typescript: "/images/coding_logo/typescript.png",
  React: "/images/coding_logo/react.png",
  Vue: "/images/coding_logo/vue.png",
  Angular: "/images/coding_logo/angular.png",
  "Next.js": "/images/coding_logo/next-js.svg",
  MATLAB: "/images/coding_logo/matlab.png"

};


const formatDuration = (duration) => {
  if (!duration) return "";
  
  const years = parseInt(duration.years) || 0;
  const months = parseInt(duration.months) || 0;
  
  const yearText = years === 1 ? "yr" : "yrs";
  const monthText = months === 1 ? "mo" : "mos";
  
  if (years && months) {
    return `${years} ${yearText}, ${months} ${monthText}`;
  } else if (years) {
    return `${years} ${yearText}`;
  } else if (months) {
    return `${months} ${monthText}`;
  }
  return "";
};

const formatSalary = (minimum, ideal) => {
  if (!minimum && !ideal) return null;
  
  // Helper to clean and parse salary values
  const cleanSalary = (val) => {
    if (!val) return null;
    // Remove any non-numeric characters and convert to number
    const num = val.toString().replace(/[^0-9.]/g, '');
    return num ? parseInt(num, 10) : null;
  };
  
  const min = cleanSalary(minimum);
  const max = cleanSalary(ideal);
  
  if (min && max) {
    if (min === max) {
      return `£${min}k`;
    } else {
      return `£${min}k - £${max}k`;
    }
  } else if (min) {
    return `£${min}k+`;
  } else if (max) {
    return `Up to £${max}k`;
  }
  
  return null;
};

/**
 * Returns a custom sponsorship/visa message depending on the "RTW doc" value.
 */
function getVisaMessage(visaStatus, fullName = "", undergradGradYear = null, postgradGradYear = null) {
  const firstName = fullName.split(" ")[0]; // e.g. "Jane Doe" -> "Jane"
  const jumpstartEmail = "operations@jumpstart-uk.com";
  const contactTeamMsg = `Contact the Jumpstart team if you have any concerns.`;

  switch (visaStatus) {
    case "Yes - Post Study Visa":
      const uGradYear = undergradGradYear ? parseInt(undergradGradYear, 10) : null;
      const pGradYear = postgradGradYear ? parseInt(postgradGradYear, 10) : null;
      
      // If EITHER graduation year is 2024 or later, the candidate is recent, so we don't show the warning.
      if ((uGradYear && uGradYear >= 2024) || (pGradYear && pGradYear >= 2024)) {
        return ""; // Don't show for 2024+ grads with PSW
      }
      return `${firstName} has a two-year post-study visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "No - I require Visa sponsorship to work in the UK":
      return `${firstName} needs visa sponsorship to work in the UK. ${contactTeamMsg}`;
    case "Yes - High Potential Individual Visa":
      return `${firstName} has a two-year high performance individual visa, so may require sponsorship in future. ${contactTeamMsg}`;
    case "Yes - Skilled Worker Visa":
    case "No - Requires Visa sponsorship":
      return `${firstName} requires visa sponsorship to work in the UK. Contact the Jumpstart team for future details.`;
    case "Other":
      return `${firstName} may require visa sponsorship, please confirm directly with the Jumpstart team if concerned.`;
    default:
      // If British or Irish passport, show nothing:
      if (
        visaStatus === "Yes - British Passport" ||
        visaStatus === "Yes - Irish Passport" ||
        visaStatus === "Yes - Settlement Status" ||
        visaStatus === "Yes - British National (Overseas) Visa" ||
        visaStatus === "Yes - Settlement Status / Indefinite Leave to Remain" ||
        visaStatus === "Yes - Dependent Visa (e.g., Spouse/Partner Visa)" ||
        visaStatus === "Yes - Ancestry Visa"

      ) {
        return "";
      }
      // Fallback for anything else:
      if (visaStatus) {
        const cleanedStatus = visaStatus.replace("Yes - ", "");
        return `${firstName} has ${cleanedStatus} and may require visa sponsorship in the future. ${contactTeamMsg}`;
      }
      return "";
  }
}

function GeneralistCard({
  candidate: rawCandidate, // Rename to avoid confusion
  recommendedCandidates,
  roles,
  shortlistedCandidates,
  setRoles,
  setRolesNeedUpdate,
  role,
  setRoleNeedUpdate,
  setNewRoleForm,
  handleSelectCandidate,
  candidatesStatus = [],
  addToast,
  recommendationData, // New prop for recommendation data
  showRecommendationInfo = false, // Flag to control whether to show recommendation info
  showInternalFields = false, // New prop to show internal recommendation fields
  displayShortlistButton, // Prop to control display of shortlist/match request buttons
  useColoredShadow = false,
  showFeaturedTag = false, // New prop to show featured tag instead of stream tag
  hideStreamTag = false, // New prop to completely hide stream/featured tag
  hideRecentlyAddedTag = false, // New prop to hide recently added tag
  // Global video state management
  currentlyPlayingVideoId,
  setCurrentlyPlayingVideoId,
}) {
  // Normalize candidate data to handle both { fields: {...} } and {...}
  // Prefer the flattened structure (from API transformCandidateFields) over the fields object
  // Check if we have transformed fields at root level (like name, image, video_intro)
  const hasTransformedFields = rawCandidate.name || rawCandidate.image !== undefined || rawCandidate.video_intro !== undefined;
  const candidate = hasTransformedFields ? rawCandidate : (rawCandidate.fields || rawCandidate);
  const candidateId = rawCandidate.id || candidate.id; // Get ID from the raw object if available

  const navigate = useNavigate();
  const { shortlistedCount, setShortlistedCount } = useShortlisted();
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [showMatchRequestRoleMenu, setShowMatchRequestRoleMenu] = useState(false);
  const [selectedRoleForModal, setSelectedRoleForModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // Add submitting state for accept/reject buttons
  const roleMenuRef = useRef();
  const matchRequestRoleMenuRef = useRef();
  const [companyLink, setCompanyLink] = useState("");
  // Use global video state instead of local state
  const isVideoPlaying = currentlyPlayingVideoId === candidateId;
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [showJumpstartNotes, setShowJumpstartNotes] = useState(false);
  const [hasMatchRequest, setHasMatchRequest] = useState(false);
  const [showVisaTooltip, setShowVisaTooltip] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [mouseDownTime, setMouseDownTime] = useState(0);
  const [showMobileRoleModal, setShowMobileRoleModal] = useState(false);
  const [showMobileRequestModal, setShowMobileRequestModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Add current user state

  // Fetch current user information
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No token found in localStorage");
          return;
        }
        
        console.log("Fetching current user in GeneralistCard...");
        const { data } = await axios.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Current user fetched in GeneralistCard:", data.user);
        setCurrentUser(data.user);
      } catch (err) {
        console.error("Failed to fetch current user in GeneralistCard", err);
      }
    };

    fetchCurrentUser();
  }, []);

  // Find candidate status from candidatesStatus array
  const candidateStatus = candidatesStatus.find(
    (candidateStatus) => candidateStatus.id === candidateId
  );

  // Check if candidate has a match request
  useEffect(() => {
    // Check if this candidate has a match request in candidatesStatus
    const hasRequest = candidatesStatus.some(
      status => status.id === candidateId && status.status === "Match requested"
    );
    setHasMatchRequest(hasRequest);
  }, [candidateId, candidatesStatus]);

  // Also check if candidateStatus indicates a match request
  useEffect(() => {
    if (candidateStatus && candidateStatus.status === "Match requested") {
      setHasMatchRequest(true);
    }
  }, [candidateStatus]);

  // Handle when another video starts playing - stop loading state if this video is no longer playing
  useEffect(() => {
    if (currentlyPlayingVideoId !== candidateId && isVideoLoading) {
      setIsVideoLoading(false);
    }
  }, [currentlyPlayingVideoId, candidateId, isVideoLoading]);

  const handleAddToShortlist = () => {
    // Check if it's mobile view
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setShowMobileRoleModal(true);
    } else {
      setShowRoleMenu(!showRoleMenu);
      if (showMatchRequestRoleMenu) setShowMatchRequestRoleMenu(false);
      if (showMatchRequestModal) setShowMatchRequestModal(false);
    }
  };

  // Handler for clicking the Match Request button
  const handleMatchRequestClick = () => {
    // If there's already a request, don't show the menu
    if (hasMatchRequest) return;
    
    // Check if it's mobile view
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setShowMobileRequestModal(true);
    } else {
      setShowMatchRequestRoleMenu(!showMatchRequestRoleMenu);
      if (showRoleMenu) setShowRoleMenu(false);
      if (showMatchRequestModal) setShowMatchRequestModal(false);
    }
  };

  // Handler for selecting a role for match request
  const handleMatchRequestRoleSelect = async (role) => {
    setRequestLoading(true);
    setSelectedRoleForModal(role);
    setShowMatchRequestRoleMenu(false);
    setShowMatchRequestModal(true);
    setTimeout(() => setRequestLoading(false), 500); // Simulate loading for modal open
  };

  const isNewCandidate = (timecreated) => {
    const createdDate = new Date(timecreated);
    const currentDate = new Date();
    return differenceInDays(currentDate, createdDate) < 7;
  };

  const handleRoleSelect = async (role, candidateIdToUpdate) => {
    setShortlistLoading(true); // Set shortlist loading state to true
    const currentShortlistedCandidates = role["Shortlisted Candidates"] || [];
    const currentCandidatesShortlistedBefore = role["Candidates Shortlisted Before"] || [];
    
    const updatedShortlistedCandidates = [
      ...new Set([...currentShortlistedCandidates, candidateIdToUpdate]),
    ];
    
    // Add to historical tracking field (no duplicates)
    const updatedCandidatesShortlistedBefore = [
      ...new Set([...currentCandidatesShortlistedBefore, candidateIdToUpdate]),
    ];

    const updatedData = {
      fields: {
        "Shortlisted Candidates": updatedShortlistedCandidates,
        "Candidates Shortlisted Before": updatedCandidatesShortlistedBefore,
      },
      roleId: role.id,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        "/api/roles/updateRoleData",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const updatedRoleData = response.data.updatedRoleData.fields;

      setRoles((prevRoles) => {
        const updatedRoles = prevRoles.map((r) =>
          r.id === updatedRoleData.id ? updatedRoleData : r
        );
        return updatedRoles;
      });

      const newUpdatedRoles = roles.map((r) =>
        r.id === updatedRoleData.id ? updatedRoleData : r
      );

      // Recalculate the shortlisted count
      const newShortlistedCount = newUpdatedRoles.reduce(
        (acc, role) =>
          acc +
          (role["Shortlisted Candidates"]
            ? role["Shortlisted Candidates"].length
            : 0),
        0
      );
      setShortlistedCount(newShortlistedCount);
      setRolesNeedUpdate(true);
      addToast(candidate.name);
    } catch (err) {
      console.error(err);
    } finally {
      setShortlistLoading(false); // Set shortlist loading state to false
      setShowRoleMenu(false);
    }
  };

  const handleRemoveCandidate = async (roles, role, candidateIdToRemove) => {
    setLoading(true); // Set loading state to true
    
    console.log("Remove candidate called with:", { 
      roles,
      roleId: role,
      candidateId: candidateIdToRemove
    });
    
    try {
      // Determine the structure of the roles data
      if (!roles) {
        console.error("Roles data is undefined or null");
        setLoading(false);
        return;
      }
      
      let roleObj;
      let currentShortlistedCandidates;
      let roleId;
      
      // Case 1: roles is an array of role objects
      if (Array.isArray(roles)) {
        console.log("Handling array format");
        roleObj = roles.find(r => r.id === role);
        if (!roleObj) {
          console.error(`Role ${role} not found in roles array`);
          setLoading(false);
          return;
        }
        currentShortlistedCandidates = roleObj["Shortlisted Candidates"] || [];
        roleId = roleObj.id;
      } 
      // Case 2: roles is an object with role names as keys
      else if (typeof roles === 'object' && roles[role] && typeof roles[role] === 'object') {
        console.log("Handling object format");
        roleObj = roles[role];
        currentShortlistedCandidates = roleObj.candidates || [];
        roleId = roleObj.roleId;
      }
      // Case 3: role itself is the role object (common in recommendation view)
      else if (typeof role === 'object' && role.id) {
        console.log("Handling role object format");
        roleObj = role;
        currentShortlistedCandidates = role["Shortlisted Candidates"] || [];
        roleId = role.id;
      }
      else {
        console.error("Unrecognized roles data structure:", { roles, role });
        setLoading(false);
        return;
      }
      
      console.log("Processing with:", { 
        roleObj, 
        currentShortlistedCandidates, 
        roleId 
      });
      
      const updatedShortlistedCandidates = currentShortlistedCandidates.filter(
        candidate => candidate !== candidateIdToRemove
      );
      
      const updatedData = {
        fields: {
          "Shortlisted Candidates": updatedShortlistedCandidates,
        },
        roleId: roleId,
      };
      
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        "/api/roles/updateRoleData",
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      const updatedRoleData = response.data.updatedRoleData.fields;
      
      // Update roles state based on the format we detected
      // Add safety checks before updating state
      if (setRoles) {
        if (Array.isArray(roles)) {
          setRoles(prevRoles => {
            if (!Array.isArray(prevRoles)) {
              console.error("Expected prevRoles to be an array but got:", prevRoles);
              return prevRoles; // Return unchanged if not an array
            }
            return prevRoles.map(r => 
              r.id === roleId 
                ? { ...r, "Shortlisted Candidates": updatedShortlistedCandidates } 
                : r
            );
          });
        } else if (typeof roles === 'object' && roles[role]) {
          setRoles(prevRoles => ({
            ...prevRoles,
            [role]: {
              ...prevRoles[role],
              candidates: updatedShortlistedCandidates,
            },
          }));
        }
      }
      
      // Update shortlisted count
      if (typeof shortlistedCount === 'number' && setShortlistedCount) {
        const newShortlistedCount = shortlistedCount - 1;
        setShortlistedCount(newShortlistedCount);
      }
      
      if (setRolesNeedUpdate) {
        setRolesNeedUpdate(true);
      }
      
    } catch (err) {
      console.error("Error removing candidate from shortlist:", err);
    } finally {
      setLoading(false);
      setShowRoleMenu(false);
    }
  };

  const isShortlistedForRole = (candidateIdToCheck, roles) => {
    console.log("isShortlistedForRole called with:", { 
      candidateId: candidateIdToCheck,
      roles
    });
    
    if (!roles) {
      console.log("Roles are null or undefined");
      return null;
    }
    
    // Check if roles is empty
    if (
      (Array.isArray(roles) && roles.length === 0) || 
      (!Array.isArray(roles) && typeof roles === 'object' && Object.keys(roles).length === 0)
    ) {
      console.log("Roles are empty, returning null");
      return null;
    }

    try {
      // Case 1: roles is an array of role objects (common in recommendation view)
      if (Array.isArray(roles)) {
        console.log("Checking array-format roles");
        for (const role of roles) {
          if (!role) continue;
          
          const shortlistedCandidates = role["Shortlisted Candidates"];
          if (
            shortlistedCandidates && 
            Array.isArray(shortlistedCandidates) &&
            shortlistedCandidates.includes(candidateIdToCheck)
          ) {
            console.log(`Candidate ${candidateIdToCheck} is shortlisted for role ${role.id}`);
            return role.id; // Return the role ID
          }
        }
        console.log(`Candidate ${candidateIdToCheck} is not shortlisted in any role in the array`);
        return null;
      }
      
      // Case 2: roles is an object with role names as keys (common in regular browse view)
      if (typeof roles === 'object') {
        console.log("Checking object-format roles");
        for (const roleName in roles) {
          const role = roles[roleName];
          if (!role) continue;
          
          // Check if the role has a candidates property that includes the candidateId
          if (
            role.candidates &&
            Array.isArray(role.candidates) &&
            role.candidates.includes(candidateIdToCheck)
          ) {
            console.log(`Candidate ${candidateIdToCheck} is shortlisted for role ${roleName}`);
            return roleName;
          }
          
          // Alternative format: role has a "Shortlisted Candidates" property
          if (
            role["Shortlisted Candidates"] &&
            Array.isArray(role["Shortlisted Candidates"]) &&
            role["Shortlisted Candidates"].includes(candidateIdToCheck)
          ) {
            console.log(`Candidate ${candidateIdToCheck} is shortlisted for role ${roleName}`);
            return roleName;
          }
        }
      }
      
      console.log(`Candidate ${candidateIdToCheck} is not shortlisted in any role`);
      return null;
    } catch (err) {
      console.error("Error in isShortlistedForRole:", err);
      return null;
    }
  };

  const roleShortlistedFor = shortlistedCandidates
    ? isShortlistedForRole(candidateId, shortlistedCandidates)
    : null;

  // Check if candidate is in MR'd Candidates for any role
  const isMatchRequestedForRole = (candidateIdToCheck, roles) => {
    if (!roles) return null;
    // Array format
    if (Array.isArray(roles)) {
      for (const role of roles) {
        if (!role) continue;
        const mrdCandidates = role["MR'd Candidates"];
        if (
          mrdCandidates &&
          Array.isArray(mrdCandidates) &&
          mrdCandidates.includes(candidateIdToCheck)
        ) {
          return role.id || role["Role Title"] || true;
        }
      }
      return null;
    }
    // Object format
    if (typeof roles === 'object') {
      for (const roleName in roles) {
        const role = roles[roleName];
        if (!role) continue;
        if (
          role["MR'd Candidates"] &&
          Array.isArray(role["MR'd Candidates"]) &&
          role["MR'd Candidates"].includes(candidateIdToCheck)
        ) {
          return roleName;
        }
      }
    }
    return null;
  };

  // Use this for the requested button
  const roleMatchRequestedFor = roles ? isMatchRequestedForRole(candidateId, roles) : null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target)) {
        setShowRoleMenu(false);
      }
      if (matchRequestRoleMenuRef.current && !matchRequestRoleMenuRef.current.contains(event.target)) {
        setShowMatchRequestRoleMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fallbackImageUrl =
    "https://cdn-icons-png.flaticon.com/512/4120/4120345.png";
  const getColorClasses = (stream) => {
    switch (stream) {
      case "Generalist":
        return {
          bgColor: "bg-emerald-100",
          textColor: "text-emerald-700",
          workBgColor: "bg-emerald-50",
          fillColor: "fill-emerald-700",
          bgColor2: "hover:bg-emerald-400",
          borderColor: "border-emerald-400",
          shadow: "shadow-[0_0_20px_rgba(16,185,129,0.3)]",
        };
      case "Sales":
        return {
          bgColor: "bg-blue-100",
          textColor: "text-blue-700",
          workBgColor: "bg-blue-50",
          fillColor: "fill-blue-700",
          bgColor2: "hover:bg-blue-400",
          borderColor: "border-blue-400",
          shadow: "shadow-[0_0_20px_rgba(59,130,246,0.3)]",
        };
      case "Software Engineering":
        return {
          bgColor: "bg-orange-100",
          textColor: "text-orange-700",
          workBgColor: "bg-orange-50",
          fillColor: "fill-orange-700",
          bgColor2: "hover:bg-orange-400",
          borderColor: "border-orange-400",
          shadow: "shadow-[0_0_20px_rgba(249,115,22,0.3)]",
        };
      case "Marketing":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-700",
          workBgColor: "bg-purple-50",
          fillColor: "fill-purple-700",
          bgColor2: "hover:bg-purple-400",
          borderColor: "border-purple-400",
          shadow: "shadow-[0_0_20px_rgba(147,51,234,0.3)]",
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          workBgColor: "bg-gray-50",
          fillColor: "fill-gray-700",
          bgColor2: "hover:bg-gray-400",
          borderColor: "border-gray-400",
          shadow: "shadow-[0_0_20px_rgba(107,114,128,0.3)]",
        };
    }
  };

  const { bgColor, textColor, workBgColor, fillColor, bgColor2, borderColor, shadow } =
    getColorClasses(candidate.stream);

  const cardClasses = useColoredShadow
    ? `shadow-xl ${shadow}`
    : 'shadow-[0_3px_10px_rgb(0,0,0,0.1)]';

  const statusStyles = {
    "Application pending": {
      color: "text-green-800",
      borderColor: "border-green-300",
      bgColor: "bg-green-50",
      message: (candidate, role) => `${candidate.name} applied to ${role}`,
    },
    Interviewing: {
      color: "text-blue-800",
      borderColor: "border-blue-300",
      bgColor: "bg-blue-50",
      message: (candidate, role) =>
        `${candidate.name} is being interviewed for ${role}`,
    },
    "Match requested": {
      color: "text-orange-800",
      borderColor: "border-orange-300",
      bgColor: "bg-orange-50",
      message: (candidate, role) =>
        `You have sent a request to ${candidate.name} for ${role}`,
    },
    "Rejected @ CV": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `You rejected ${candidate.name} for ${role}`,
    },
    "Rejected @ Interview": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `You rejected ${candidate.name} for ${role}`,
    },
    "Rejected Interview Request": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) =>
        `${candidate.name} declined invite to ${role}`,
    },
    "Company withdrawn": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) => `You withdrew from ${role}`,
    },
    "Candidate withdrawn": {
      color: "text-red-800",
      borderColor: "border-red-300",
      bgColor: "bg-red-50",
      message: (candidate, role) => `${candidate.name} withdrew from ${role}`,
    },
  };

  const statusStyle = candidateStatus
    ? statusStyles[candidateStatus.status]
    : null;

  const newRole = () => {
    handleSelectCandidate(candidate);
    setNewRoleForm(true);
  };

  useEffect(() => {
    const linkFromStorage = localStorage.getItem("companyLink");
    
    setCompanyLink(linkFromStorage);
  }, []);

  const shouldDisplayShortlistButton = displayShortlistButton !== undefined 
    ? displayShortlistButton 
    : (!candidateStatus ||
       [
         "Rejected @ CV",
         "Rejected @ Interview",
         "Rejected Interview Request",
         "Company withdrawn",
         "Candidate withdrawn",
       ].includes(candidateStatus.status));

  const handleSvgClick = () => {
    setSvgMovedUp(true);
    setShowCopiedText(true);
    setTimeout(() => {
      setSvgMovedUp(false);
      setShowCopiedText(false);
    }, 2000);
  };

  const [showCopiedText, setShowCopiedText] = useState(false);
  const [svgMovedUp, setSvgMovedUp] = useState(false);

  const copyToClipboard = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up to parent elements
    let baseLink = companyLink || window.location.origin;
    try {
      const urlObj = new URL(baseLink);
      baseLink = urlObj.origin;
    } catch (err) {
      baseLink = window.location.origin;
    }
    const link = `${baseLink}/candidate/${candidate.url_id}`;
    navigator.clipboard.writeText(link).then(
      () => {
        handleSvgClick();
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const validRightToWorkStatuses = [
    "No - I don't have the right to work in the UK",
    "No - Requires Visa sponsorship",
    "No - Looking for Visa sponsorship, but willing to self-sponsor if necessary",
    "No - Requires visa sponsorship - is well versed in the process + has a lawyer at hand if needed",
  ];

  const workingStyleMapping = Array.isArray(candidate.working_style)
    ? candidate.working_style.map((style) => {
        if (style === "5 days a week in office") {
          return "Fully in office";
        } else if (style === "Hybrid (1-4 days in office)") {
          return "Hybrid";
        } else if (style === "Fully remote") {
          return "Fully remote";
        }
        return style;
      })
    : [];

  // Get all the candidate data from the recommendationData prop
  const candidateRecommendationData = recommendationData || 
    (candidate.recommendationScore ? 
      { 
        score: candidate.recommendationScore || 0, 
        filtered: candidate.isFiltered || false, 
        reason: candidate.recommendationReason || "" 
      } : null);
  
  // Determine match quality level based on score
  const getMatchQuality = (score) => {
    if (score >= 80) return { label: "Excellent Match", color: "text-[#028d72]", bgColor: "bg-[#e6f7f4]" };
    if (score >= 60) return { label: "Good Match", color: "text-[#02B491]", bgColor: "bg-[#e6f7f4]" };
    if (score >= 40) return { label: "Match", color: "text-[#40c4ac]", bgColor: "bg-[#e6f7f4]" };
    return { label: "Match", color: "text-amber-700", bgColor: "bg-amber-50" };
  };
  
  // Get match quality for this candidate
  const matchQuality = candidateRecommendationData?.score 
    ? getMatchQuality(candidateRecommendationData.score) 
    : candidateRecommendationData 
      ? getMatchQuality(0) // Default match if we have recommendation data but no score
      : null;

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isRecommendation = queryParams.get('recommendation') === 'true';
    const roleId = queryParams.get('roleId');
    const pendingRoleId = localStorage.getItem("pendingRecommendationRoleId");
    
    if ((isRecommendation && roleId) || pendingRoleId) {
      const targetRoleId = roleId || pendingRoleId;
      localStorage.removeItem("pendingRecommendationRoleId");
      
      // Add defensive check to make sure roles is an array before using find
      if (roles && Array.isArray(roles) && roles.length > 0) {
        const role = roles.find(r => r.id === targetRoleId);
        if (role) {
          // Your original logic here...
        }
      }
    }
  }, []);

  // Helper function to get the embedded video URL
  const getEmbeddedVideoUrl = (url) => {
    if (!url) return null;
    
    // Handle Loom videos
    if (url.includes('loom.com')) {
      // Convert from https://www.loom.com/share/[id] to https://www.loom.com/embed/[id]
      return url.replace('/share/', '/embed/');
    }
    
    // Handle Vimeo videos
    if (url.includes('vimeo.com')) {
      // Extract the Vimeo ID
      const vimeoId = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoId && vimeoId[1]) {
        return `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Handle YouTube videos
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract YouTube ID
      let youtubeId = '';
      if (url.includes('youtube.com/watch')) {
        const searchParams = new URL(url).searchParams;
        youtubeId = searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        youtubeId = url.split('youtu.be/')[1];
      }
      
      if (youtubeId) {
        return `https://www.youtube.com/embed/${youtubeId}`;
      }
    }
    
    // Return original URL if we can't transform it
    return url;
  };

  // Toggle video playback
  const toggleVideo = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    if (!isVideoPlaying) {
      setIsVideoLoading(true); // Set loading state when starting to play
      setCurrentlyPlayingVideoId(candidateId); // Set this video as currently playing
    } else {
      setCurrentlyPlayingVideoId(null); // Stop this video
    }
  };
  
  // Handle video loaded
  const handleVideoLoaded = () => {
    setIsVideoLoading(false);
  };

  // Callback to update roles state after a request is sent
  const handleRequestSent = (candidateId, roleId) => {
    if (!setRoles || !roles) return;
    setRoles(prevRoles => {
      if (!Array.isArray(prevRoles)) return prevRoles;
      return prevRoles.map(role => {
        if (role.id === roleId) {
          const prevMR = Array.isArray(role["MR'd Candidates"]) ? role["MR'd Candidates"] : [];
          if (!prevMR.includes(candidateId)) {
            return {
              ...role,
              "MR'd Candidates": [...prevMR, candidateId],
            };
          }
        }
        return role;
      });
    });
  };

  // Local state to track application status changes
  const [localApplicationStatus, setLocalApplicationStatus] = useState(null);
  
  // Check if candidate has applied (has Application pending status)
  const currentStatus = localApplicationStatus || (candidateStatus && candidateStatus.status);
  const hasApplied = currentStatus === "Application pending";
  const isAccepted = currentStatus === "Interviewing";
  const isRejected = currentStatus === "Rejected @ CV";
  
  // Handle accept/reject for applied candidates
  const handleSubmit = async (stage) => {
    if (!hasApplied) return;
    
    // Find the interview record from candidatesStatus
    const interviewRecord = candidatesStatus.find(cs => cs.id === candidateId && cs.status === "Application pending");
    if (!interviewRecord) {
      console.error("Interview record not found for candidate", candidateId);
      return;
    }
    
    const updatedData = {
      fields: { Stage: stage },
      interviewId: interviewRecord.interviewId,
    };

    // Add user tracking fields based on the action
    if (currentUser && currentUser.id && !currentUser.isLegacyLogin) {
      if (stage === "Interviewing") {
        updatedData.fields["Accepted By"] = [currentUser.id];
        console.log(`Application accepted by user: ${currentUser.firstName || currentUser.email} (ID: ${currentUser.id})`);
      } else if (stage === "Rejected @ CV") {
        updatedData.fields["Rejected By"] = [currentUser.id];
        console.log(`Application rejected by user: ${currentUser.firstName || currentUser.email} (ID: ${currentUser.id})`);
      }
    } else if (currentUser && currentUser.isLegacyLogin) {
      console.log("Action by legacy/internal user - not tracking individual user");
    } else {
      console.log("No current user found - action will not be tracked to specific user");
    }
    
    setSubmitting(true);
    try {
      await axios.patch(`/api/interviews/updateInterviewRequest`, updatedData);
      console.log("Interview request updated successfully");
      
      // Update local status immediately
      setLocalApplicationStatus(stage);
      
      // Update the candidate status locally to reflect the change
      if (setRolesNeedUpdate) {
        setRolesNeedUpdate(true);
      }
      // Show success message
      if (addToast) {
        const actionText = stage === "Interviewing" ? "accepted" : "rejected";
        const roleName = candidateStatus?.role || 'the role';
        addToast(`${candidate.name} ${actionText} for ${roleName}`, stage === "Interviewing" ? "success" : "reject");
      }
    } catch (err) {
      console.error("Error updating interview request:", err);
      if (addToast) {
        addToast("Error updating application", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle card click with text selection detection
  const handleCardClick = (e) => {
    // Don't navigate if clicking on interactive elements
    if (
      e.target.closest('button') || 
      e.target.closest('img[style*="cursor: pointer"]') ||
      e.target.closest('.video-player-container') ||
      e.target.closest('.video-play-trigger') ||
      e.target.closest('svg[style*="cursor: pointer"]') ||
      e.target.closest('a') ||
      e.target.closest('[onclick]')
    ) {
      return;
    }

    // Check if user has selected text
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return; // Don't navigate if text is selected
    }

    // Check if this was a drag operation (text selection attempt)
    const clickDuration = Date.now() - mouseDownTime;
    if (clickDuration > 200) { // If mouse was down for more than 200ms, likely a selection
      return;
    }

    // Store the current page before opening the candidate details in a new tab
    sessionStorage.setItem("lastVisitedPage", window.location.pathname);
    
    // Navigate to candidate detail page
    window.open(`/candidate/${candidate.url_id || candidateId}`, "_blank");
  };

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setMouseDownTime(Date.now());
  };

  const handleMouseUp = (e) => {
    setIsMouseDown(false);
  };

  return (
    <div 
      className={`relative max-w-xl mt-5 bg-white rounded-xl overflow-hidden flex flex-col h-full ${cardClasses} ${candidateRecommendationData?.filtered ? 'border-2 border-amber-400' : ''}`}
      onClick={handleCardClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={{ cursor: 'pointer' }}
    >
      {/* Mobile Action Buttons Container */}
      {(shouldDisplayShortlistButton || isRejected) && !hideStreamTag && (
        <div className="relative h-12 md:hidden">
          <div className="absolute top-2 right-6 z-20">
            <div className="flex gap-2">
              {roleShortlistedFor ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCandidate(
                      shortlistedCandidates,
                      roleShortlistedFor,
                      candidateId
                    );
                  }}
                  className="px-3 py-2 text-xs border border-red-400 text-white bg-red-500 rounded-full font-medium shadow-lg backdrop-blur-sm bg-opacity-90 hover:bg-red-600 transition-all duration-200 flex items-center gap-1"
                  disabled={loading}
                >
                  {loading ? (
                    <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-3 h-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Remove from shortlist
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* Mobile Request Button - Now on the left */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatchRequestClick();
                    }}
                    className={`px-3 py-2 text-xs font-medium rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 flex items-center gap-1 ${
                      roleMatchRequestedFor 
                        ? 'border border-green-400 bg-green-500 bg-opacity-90 text-white hover:bg-green-600' 
                        : 'bg-emerald-500 bg-opacity-90 text-white border border-emerald-400 hover:bg-emerald-600'
                    }`}
                    disabled={requestLoading || roleMatchRequestedFor}
                    title={roleMatchRequestedFor ? "You have already sent a match request to this candidate" : "Send a match request to this candidate"}
                  >
                    {requestLoading ? (
                      <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : roleMatchRequestedFor ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Request sent
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                          />
                        </svg>
                        Request
                      </>
                    )}
                  </button>

                  {/* Mobile Shortlist Button - Now on the right, icon only */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToShortlist();
                    }}
                    className={`p-2 backdrop-blur-sm rounded-full font-medium shadow-lg transition-all duration-200 flex items-center justify-center ${
                      roleShortlistedFor 
                        ? 'text-white bg-green-500 bg-opacity-90 border border-green-400 hover:bg-green-600' 
                        : 'text-gray-700 bg-white bg-opacity-90 border border-gray-200 hover:bg-emerald-500 hover:text-white hover:border-emerald-500'
                    }`}
                    title={roleShortlistedFor ? "Already shortlisted" : "Add to shortlist"}
                  >
                    {shortlistLoading ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : roleShortlistedFor ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                        />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOP: Candidate image & "Recently added"/"Recommended" labels */}
      <div className="relative pt-2 px-4 md:pt-4">
        {/* Mobile Action Buttons - Floating over image - Only visible on mobile screens */}
        {(shouldDisplayShortlistButton || isRejected) && hideStreamTag && (
          <div className="absolute top-6 right-6 md:hidden z-20">
            <div className="flex gap-2">
              {roleShortlistedFor ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCandidate(
                      shortlistedCandidates,
                      roleShortlistedFor,
                      candidateId
                    );
                  }}
                  className="px-3 py-2 text-xs border border-red-400 text-white bg-red-500 rounded-full font-medium shadow-lg backdrop-blur-sm bg-opacity-90 hover:bg-red-600 transition-all duration-200 flex items-center gap-1"
                  disabled={loading}
                >
                  {loading ? (
                    <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-3 h-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Remove from shortlist
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* Mobile Request Button - Now on the left */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatchRequestClick();
                    }}
                    className={`px-3 py-2 text-xs font-medium rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 flex items-center gap-1 ${
                      roleMatchRequestedFor 
                        ? 'border border-green-400 bg-green-500 bg-opacity-90 text-white hover:bg-green-600' 
                        : 'bg-emerald-500 bg-opacity-90 text-white border border-emerald-400 hover:bg-emerald-600'
                    }`}
                    disabled={requestLoading || roleMatchRequestedFor}
                    title={roleMatchRequestedFor ? "You have already sent a match request to this candidate" : "Send a match request to this candidate"}
                  >
                    {requestLoading ? (
                      <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : roleMatchRequestedFor ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Request sent
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                          />
                        </svg>
                        Request
                      </>
                    )}
                  </button>

                  {/* Mobile Shortlist Button - Now on the right, icon only */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToShortlist();
                    }}
                    className={`p-2 backdrop-blur-sm rounded-full font-medium shadow-lg transition-all duration-200 flex items-center justify-center ${
                      roleShortlistedFor 
                        ? 'text-white bg-green-500 bg-opacity-90 border border-green-400 hover:bg-green-600' 
                        : 'text-gray-700 bg-white bg-opacity-90 border border-gray-200 hover:bg-emerald-500 hover:text-white hover:border-emerald-500'
                    }`}
                    title={roleShortlistedFor ? "Already shortlisted" : "Add to shortlist"}
                  >
                    {shortlistLoading ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : roleShortlistedFor ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                        />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Video or Image Container */}
        <div className="relative w-full h-64 md:h-72 rounded-xl overflow-hidden">
          {isVideoPlaying && candidate.video ? (
            <div 
              className="video-player-container w-full h-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              {isVideoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                  <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
              <iframe
                src={getEmbeddedVideoUrl(candidate.video)}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`${candidate.name} video introduction`}
                onLoad={handleVideoLoaded}
              ></iframe>
              {/* Close button */}
              <button 
                className="absolute top-3 right-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-1 z-10"
                onClick={toggleVideo}
              >
                <svg 
                  className="h-6 w-6 text-white" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ) : (
            <>
              <img
                className="w-full h-72 object-cover rounded-xl"
                src={candidate.image || fallbackImageUrl}
                alt="Candidate"
                onError={(e) => {
                  console.log(`Image failed to load for candidate: ${candidate.name} (${candidateId})`);
                  e.target.src = fallbackImageUrl;
                }}
              />
              {/* Play button overlay - only show if candidate has a video */}
              {candidate.video && (
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer video-play-trigger"
                  onClick={toggleVideo}
                >
                  <div className="relative bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-5 transition-all duration-300 transform hover:scale-110">
                    <svg 
                      className="h-14 w-14 text-white" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!hideStreamTag && (
          <span
            className={`absolute top-6 right-6 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${
              showFeaturedTag 
                ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                : `${bgColor} ${textColor}`
            }`}
          >
            {showFeaturedTag ? (
              <svg
                className="h-4 w-4 text-amber-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ) : (
              <svg
                className={`h-1.5 w-1.5 ${fillColor}`}
                viewBox="0 0 6 6"
                aria-hidden="true"
              >
                <circle cx={3} cy={3} r={3} />
              </svg>
            )}
            {showFeaturedTag ? 'Featured' : candidate.stream}
          </span>
        )}

        {/* Add warning for filtered candidates */}
        {candidateRecommendationData?.filtered && (
          <span
            className="text-amber-800 bg-amber-100 absolute bottom-6 left-6 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium border border-amber-400"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-5 h-5"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" 
              />
            </svg>
            Doesn't meet requirements
          </span>
        )}

        {!hideRecentlyAddedTag && isNewCandidate(candidate.createdTime) && (
          <span
            className="text-black absolute top-6 left-6 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium"
            style={{
              background: "white",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            Recently added
          </span>
        )}

        {recommendedCandidates && recommendedCandidates.includes(candidateId) && (
          <span
            className="text-black absolute top-6 left-6 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium"
            style={{
              background: "white",
            }}
          >
            <img
              src={jumpstart}
              title="Jumpstart icon"
              className="h-6 w-6 rounded-sm"
            />
            Recommended for you
          </span>
        )}
        
        {/* AI Recommendation Score */}
        {candidateRecommendationData && matchQuality && (
          <div className="absolute top-[15rem] right-6 shadow-md">
            <div className={`flex items-center ${matchQuality.bgColor} ${matchQuality.color} px-3 py-1.5 rounded font-semibold border border-gray-200`}>
              <svg className="w-5 h-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {matchQuality.label}
            </div>
          </div>
        )}


      </div>

      {/* Name and Salary Range - Adding this section */}
      <div className="mx-4 mt-4 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h5 className="text-xl font-medium tracking-tight text-gray-900 truncate">
              {candidate.name}
            </h5>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className={`w-6 h-6 ml-1 cursor-pointer transform flex-shrink-0 ${
                svgMovedUp ? "hidden" : ""
              }`}
              onClick={copyToClipboard}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6"
              />
            </svg>
            {showCopiedText && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 ml-1 flex-shrink-0"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}

              <img
                src={linkedin}
              alt={candidate.name}
              title="Linkedin icon"
              className="h-6 w-6 ml-2 rounded-sm flex-shrink-0"
                onClick={(e) => {
                e.preventDefault();
                  let url = candidate.linkedin;
                if (url && !url.startsWith("https://")) {
                    url = "https://" + url;
                  }
                  window.open(url, "_blank");
                }}
                style={{ cursor: "pointer" }}
              />
            {candidate.cv && (
              <img
                src={cv}
                alt={candidate.cv}
                title="CV icon"
                className="h-6 w-6 ml-2 flex-shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.cv, "_blank");
                }}
                style={{ cursor: "pointer" }}
              />
            )}
  
            {candidate.github && (
              <img
                src={github}
                alt={candidate.github}
                title="Github icon"
                className="h-6 w-6 ml-2 flex-shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.github, "_blank");
                }}
                style={{ cursor: "pointer" }}
              />
            )}

            {/* New portfolio icon */}
            {candidate.portfolio_swe && (
              <svg
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.portfolio_swe, "_blank");
                }}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 cursor-pointer ml-2 flex-shrink-0"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 
                    1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                />
              </svg>
            )}

            {/* New marketing portfolio icon */}
            {candidate.marketing_portfolio && (
              <svg
                onClick={(e) => {
                  e.preventDefault();
                  window.open(candidate.marketing_portfolio, "_blank");
                }}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6 cursor-pointer ml-2 flex-shrink-0"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 
                    1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                />
              </svg>
            )}

          </div>
          {/* Expected salary */}
          <div className="flex flex-col text-right">
            <span className="text-sm text-gray-500">Expected Salary Range:</span>
            <span className="text-sm font-semibold">
              {formatSalary(candidate.salary_minimum || candidate.minimum_salary, candidate.salary_ideal || candidate.ideal_salary)}
            </span>
          </div>
        </div>
      </div>
{/* Jumpstart view (Sales) field - collapsible */}
{candidate["Jumpstart view (Sales)"] && (
  <div className="mx-4 mb-4">
    <div 
      className={`rounded-lg overflow-hidden transition-all duration-300 ${
        showJumpstartNotes 
          ? 'shadow-md border border-emerald-100' 
          : 'border border-transparent hover:border-emerald-100'
      }`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent card navigation
        setShowJumpstartNotes(!showJumpstartNotes);
      }}
      onMouseEnter={() => setShowJumpstartNotes(true)}
      onMouseLeave={() => setShowJumpstartNotes(false)}
    >
      {/* Header */}
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer ${
          showJumpstartNotes 
            ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/30' 
            : 'bg-emerald-50/60 hover:bg-emerald-50'
        }`}
      >
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-emerald-100">
            <img
              src={jumpstart}
              alt="Jumpstart"
              className="h-4 w-4"
            />
          </div>
          <span className="font-medium text-sm text-gray-800">Jumpstart's POV</span>
        </div>
        <div className={`rounded-full p-1 ${showJumpstartNotes ? 'bg-emerald-100/80' : 'bg-transparent'}`}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={2} 
            stroke="currentColor" 
            className={`w-4 h-4 text-emerald-600 transition-transform duration-300 ${
              showJumpstartNotes ? 'rotate-180 transform' : ''
            }`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </div>
      
      {/* Content - animate height */}
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          showJumpstartNotes ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="p-3 pt-2 bg-white border-t border-emerald-100">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
            {candidate["Jumpstart view (Sales)"]}
          </p>
        </div>
      </div>
    </div>
  </div>
)}
      {/* Clean, single recommendation reason display */}
      {showRecommendationInfo && candidateRecommendationData && candidateRecommendationData.reason && (
        <div className="mx-4 mt-2 mb-3 border-l-4 border-[#02B491] bg-[#e6f7f4] rounded-md p-3 shadow-sm">
          <div className="flex items-start">
            <svg className="w-5 h-5 mr-2 text-[#02B491] flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <div>
              <p className="font-medium text-sm text-[#02B491]">
                Why it's {/^[aeiou]/i.test(matchQuality?.label.toLowerCase() || 'match') ? 'an' : 'a'} {matchQuality?.label.toLowerCase() || 'match'}:
              </p>
              <p className="text-sm text-gray-700">
                {candidateRecommendationData.reason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MIDDLE: flex-1 so it stretches; all details, status, etc. */}
      <div className="flex-1 pt-2 px-4 flex flex-col">
        {/* Roles, etc. */}
        <div className="flex flex-wrap">
          {candidate.roles &&
            candidate.roles.map((role, index) => (
              <span
                key={index}
                className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
              >
                {role}
              </span>
            ))}
          {candidate.sales && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.sales}
            </span>
          )}
          {candidate.swe_roles &&
            candidate.swe_roles.map((role, index) => (
              <span
                key={index}
                className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
              >
                {role}
              </span>
            ))}
          {candidate.exp_swe && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.exp_swe}
            </span>
          )}
          {candidate.time_coding && (
            <span
              className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
            >
              {candidate.time_coding} yrs of coding exp
            </span>
          )}

          {/* Right to work checks */}
          {candidate.right_to_work &&
            validRightToWorkStatuses.includes(candidate.right_to_work) && (
              <span
                className={`inline-flex mb-2 mr-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-md font-medium ${bgColor} ${textColor}`}
              >
                {candidate.right_to_work}
              </span>
            )}
        </div>

        {/* Visa Status Component - Styled like Jumpstart POV */}
        {candidate.right_to_work && getVisaMessage(candidate.right_to_work, candidate.name, candidate.undergrad_graduation_year, candidate.postgrad_graduation_year) && (
          <div className="mb-4">
            <div 
              className={`rounded-lg overflow-hidden transition-all duration-300 ${
                showVisaTooltip 
                  ? 'shadow-md border border-gray-100' 
                  : 'border border-transparent hover:border-gray-100'
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent card navigation
                setShowVisaTooltip(!showVisaTooltip);
              }}
              onMouseEnter={() => setShowVisaTooltip(true)}
              onMouseLeave={() => setShowVisaTooltip(false)}
            >
              {/* Header */}
              <div 
                className={`flex items-center justify-between p-3 cursor-pointer ${
                  showVisaTooltip 
                    ? 'bg-gradient-to-r from-gray-50 to-gray-100/30' 
                    : 'bg-gray-50/60 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded bg-gray-100">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-sm text-gray-800">Visa Status</span>
                </div>
                <div className={`rounded-full p-1 ${showVisaTooltip ? 'bg-gray-100/80' : 'bg-transparent'}`}>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
                      showVisaTooltip ? 'rotate-180 transform' : ''
                    }`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </div>
              </div>
              
              {/* Content - animate height */}
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  showVisaTooltip ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="p-3 pt-2 bg-white border-t border-gray-100">
                  <p 
                    className="text-sm text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: getVisaMessage(candidate.right_to_work, candidate.name, candidate.undergrad_graduation_year, candidate.postgrad_graduation_year) }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coding languages */}
        <div className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md text-black text-sm md:text-md font-medium flex gap-x-2">
          {candidate.coding_languages &&
            candidate.coding_languages.map((language, index) => (
              <img
                key={index}
                src={languageLogos[language.trim()] || "path/to/default-logo.png"}
                alt={language}
                className="h-6 w-6"
              />
            ))}
        </div>

        {/* Working style & languages */}
        <div className="flex pb-2 pt-1">
          {candidate.working_style &&
            Array.isArray(candidate.working_style) &&
            workingStyleMapping.length > 0 && (
              <p className="pr-5 font-normal flex text-sm text-black items-center">
                <img className="h-6 pr-1" src={Office} alt="Office icon" />
                {workingStyleMapping.join(" or ")}
              </p>
            )}
          {candidate.languages &&
            Array.isArray(candidate.languages) &&
            candidate.languages.some((language) => language !== "N/A") && (
              <p className="font-normal flex text-sm text-black items-center">
                <img className="h-6 pr-2" src={Languages} alt="Languages icon" />
                {candidate.languages
                  .filter((language) => language !== "N/A")
                  .join(", ")}
              </p>
            )}
        </div>

        {/* Degrees */}
        {candidate.undergrad_degree_type && (
          <p className="text-sm">
            <span className="font-semibold">
              {candidate.undergrad_degree_type}{" "}
              {candidate.undergrad_degree_subject}
            </span>
            {candidate.undergrad_university && " @ "}
            {candidate.undergrad_university}
            {(candidate.undergrad_result || candidate.undergrad_graduation_year) && " ("}
            {candidate.undergrad_result}
            {candidate.undergrad_result && candidate.undergrad_graduation_year && " - "}
            {candidate.undergrad_graduation_year}
            {(candidate.undergrad_result || candidate.undergrad_graduation_year) && ")"}
          </p>
        )}

        {candidate.postgrad_degree_type && (
          <p className="text-sm pt-2">
            <span className="font-semibold">
              {candidate.postgrad_degree_type}{" "}
              {candidate.postgrad_degree_subject}
            </span>
            {candidate.postgrad_university && " @ "}
            {candidate.postgrad_university}
            {(candidate.postgrad_result || candidate.postgrad_graduation_year) && " ("}
            {candidate.postgrad_result}
            {candidate.postgrad_result && candidate.postgrad_graduation_year && " - "}
            {candidate.postgrad_graduation_year}
            {(candidate.postgrad_result || candidate.postgrad_graduation_year) && ")"}
          </p>
        )}

        {/* Work experience summary */}
        <div className={`pb-2 mb-2 px-2 rounded-md ${workBgColor}`}>
          {candidate.recentTarget && (
            <div className="font-normal text-sm text-gray-700 pt-2">
              <span className="font-semibold text-black">
                Recent Target Achieved:
              </span>{" "}
              {candidate.recentTarget}
            </div>
          )}

{candidate.work_experience_1_company && (
          <>
            <p className="text-sm pt-2">
              <span className="font-semibold">
                {candidate.work_experience_1_role}
              </span>{" "}
              @ {candidate.work_experience_1_company}
              {candidate.work_experience_1_duration && 
                ` (${formatDuration(candidate.work_experience_1_duration)})`}
            </p>
            <p className="text-gray-500 text-sm">
              {candidate.work_experience_1_description}
            </p>
          </>
        )}

        {candidate.work_experience_2_company && (
          <>
            <p className="text-sm pt-2">
              <span className="font-semibold">
                {candidate.work_experience_2_role}
              </span>{" "}
              @ {candidate.work_experience_2_company}
              {candidate.work_experience_2_duration && 
                ` (${formatDuration(candidate.work_experience_2_duration)})`}
            </p>
            <p className="text-gray-500 text-sm">
              {candidate.work_experience_2_description}
            </p>
          </>
        )}


      </div>

        {/* Internal Fields - Only show in InternalRecommendations */}
        {showInternalFields && (
          <div className="mt-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
            <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4 mr-1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423L16.5 15.75l.394 1.183a2.25 2.25 0 001.423 1.423L19.5 18.75l-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
              other extra fields 
            </h4>
            
            {(() => {
              const parseMultiSelectField = (field) => {
                if (!field) return [];
                if (Array.isArray(field)) return field;
                if (typeof field === 'string') {
                  // Handle comma-separated values or other separators
                  return field.split(/[,;|]/).map(item => item.trim()).filter(item => item.length > 0);
                }
                return [];
              };

              const industriesData = candidate.industries_interested || rawCandidate.fields?.["Industries most interested in copy"];
              const generalistRolesData = candidate.generalist_roles_suitable || rawCandidate.fields?.["Generalist roles suitable for"];
              const generalistRolesCopyData = candidate.generalist_roles_suitable_copy || rawCandidate.fields?.["Generalist roles suitable for copy"];
              const salesRolesData = candidate.sales_roles_suitable || rawCandidate.fields?.["Sales roles suitable for"];

              const industries = parseMultiSelectField(industriesData);
              const generalistRoles = parseMultiSelectField(generalistRolesData);
              const generalistRolesCopy = parseMultiSelectField(generalistRolesCopyData);
              const salesRoles = parseMultiSelectField(salesRolesData);

              return (
                <div className="space-y-3">
                  {industries.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-indigo-700 mb-1 block">Industries Interested:</span>
                      <div className="flex flex-wrap gap-1">
                        {industries.map((industry, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                            {industry}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {generalistRoles.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-indigo-700 mb-1 block">Generalist Roles Suitable:</span>
                      <div className="flex flex-wrap gap-1">
                        {generalistRoles.map((role, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {generalistRolesCopy.length > 0 && generalistRolesCopy.join('') !== generalistRoles.join('') && (
                    <div>
                      <span className="text-xs font-medium text-indigo-700 mb-1 block">Alternative Roles:</span>
                      <div className="flex flex-wrap gap-1">
                        {generalistRolesCopy.map((role, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {salesRoles.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-indigo-700 mb-1 block">Sales Roles Suitable:</span>
                      <div className="flex flex-wrap gap-1">
                        {salesRoles.map((role, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {industries.length === 0 && generalistRoles.length === 0 && generalistRolesCopy.length === 0 && salesRoles.length === 0 && (
                    <div className="text-center py-2">
                      <span className="text-xs text-gray-500 italic">No additional insights available</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Candidate description */}
        {candidate.description && (
          <p className="mt-2 font-normal text-sm text-gray-700 flex">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.7"
                stroke="currentColor"
                className="size-5 mr-1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1-3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                />
              </svg>
            </div>
            {candidate.description}
          </p>
        )}

        {/* Most impressive swe */}
        {candidate.most_impressive_swe && (
          <div className="mt-2 mb-4 flex">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1-3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
              />
            </svg>
            <p className="font-normal pl-1 text-sm text-gray-700">
              {candidate.most_impressive_swe}
            </p>
          </div>
        )}
        
        {/* Status display (if any) - Updated to show current status including local changes */}
        {(() => {
          // Use current status (including local changes) to determine what to display
          const displayStatus = currentStatus || (candidateStatus && candidateStatus.status);
          const displayStatusStyle = displayStatus ? statusStyles[displayStatus] : null;
          
          if (!displayStatusStyle) return null;
          
          return (
            <div
              className={`flex justify-between items-center py-2 px-2 ${
                shouldDisplayShortlistButton || isRejected ? "mt-2" : "my-4"
              } text-sm ${displayStatusStyle.color} border ${displayStatusStyle.borderColor} rounded-lg ${displayStatusStyle.bgColor}`}
              role="alert"
            >
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 inline w-4 h-4 mr-1"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                </svg>
                <span className="sr-only">Info</span>
                <div>{displayStatusStyle.message(candidate, candidateStatus?.role || 'Unknown Role')}</div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* FOOTER: Accept/Reject buttons for applied candidates OR Shortlist and Match Request buttons */}
      {hasApplied ? (
        // Show Accept/Reject buttons for applied candidates
        <div className="p-4">
          <div className="flex gap-2">
            {submitting ? (
              <div className="flex justify-center items-center w-full h-12 bg-gray-100 rounded-lg">
                <img src={Loading} className="h-6" alt="Loading" />
              </div>
            ) : (
              <>
                {/* Accept Interview Request Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click navigation
                    handleSubmit("Interviewing");
                  }}
                  className="w-3/5 group px-3 py-2 text-sm text-black items-center rounded-lg font-medium transform hover:bg-white bg-[#02B491] hover:text-black text-white transition duration-400 border-2 border-[#02B491] flex flex-col justify-center items-center"
                  title="We'll introduce you via email."
                >
                  <span className="text-white group-hover:text-black text-center">
                    Accept Interview Request
                  </span>
                  <p className="flex items-center justify-center mt-1 text-white font-normal text-xs group-hover:text-black">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-4 h-4 mr-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0118 0Zm-9-3.75h.008v.008H12V8.25Z"
                      />
                    </svg>
                    You'll be introduced over email!
                  </p>
                </button>

                {/* Not a Match Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click navigation
                    handleSubmit("Rejected @ CV");
                  }}
                  className="w-2/5 px-3 group py-2 text-sm whitespace-nowrap rounded-lg font-medium transform bg-white hover:bg-gray-500 text-black hover:text-white transition duration-400 border-2 border-gray-500"
                >
                  Not a match
                </button>
              </>
            )}
          </div>
        </div>
      ) : isAccepted ? (
        // Show accepted status (no buttons - action is final)
        <div className="p-4">
          <div className="w-full px-4 py-3 bg-green-50 border-2 border-green-300 rounded-lg text-center">
            <div className="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 text-green-600 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-green-800 font-medium">Application Accepted</span>
            </div>
            <p className="text-green-600 text-xs mt-1">You'll be introduced over email</p>
          </div>
        </div>
      ) : (shouldDisplayShortlistButton || isRejected) ? (
        // Show regular shortlist/request buttons for non-applied candidates - Hidden on mobile
        <div className="hidden md:block p-4">
          {roleShortlistedFor ? (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click navigation
                handleRemoveCandidate(
                  shortlistedCandidates,
                  roleShortlistedFor,
                  candidateId
                );
              }}
              className="w-full px-6 py-2 text-sm border-2 border-red-500 text-black rounded-lg font-medium transform hover:bg-red-500 hover:text-white"
              disabled={loading}
            >
              {loading ? (
                <div className="flex justify-center items-center w-full rounded-lg">
                  <img
                    src={LoadingBlack}
                    className="h-6 fill-red-500"
                    alt="Loading"
                  />
                </div>
              ) : (
                `Remove from ${typeof roleShortlistedFor === 'object' && roleShortlistedFor.title ? roleShortlistedFor.title : roleShortlistedFor} shortlist`
              )}
            </button>
          ) : (
            <div className="flex gap-2"> {/* Container for side-by-side buttons */}
              {/* Shortlist Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click navigation
                  handleAddToShortlist();
                }}
                className={`w-1/2 px-3 py-2 text-sm text-black flex rounded-lg font-medium justify-center ${bgColor2} hover:text-white border-2 ${borderColor} bg-white`} 
              >
                {shortlistLoading ? (
                  <div className="flex justify-center items-center w-full rounded-lg">
                    <img src={LoadingBlack} className="h-6" alt="Loading" />
                  </div>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-5 mr-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"
                      />
                    </svg>
                    Add to shortlist
                  </>
                )}
              </button>

              {/* Match Request Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click navigation
                  handleMatchRequestClick();
                }}
                className={`w-1/2 px-3 py-2 text-sm text-black flex rounded-lg font-medium justify-center 
                  ${roleMatchRequestedFor 
                    ? 'border-2 border-red-500 hover:bg-red-500 hover:text-white' 
                    : 'hover:bg-emerald-400 hover:text-white border-2 border-emerald-400'} 
                  bg-white`}
                disabled={requestLoading || roleMatchRequestedFor}
                title={roleMatchRequestedFor ? "You have already sent a match request to this candidate" : "Send a match request to this candidate"}
              >
                {requestLoading ? (
                  <div className="flex justify-center items-center w-full rounded-lg">
                    <img src={LoadingBlack} className="h-6" alt="Loading" />
                  </div>
                ) : roleMatchRequestedFor ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-5 mr-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Requested
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-5 mr-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                      />
                    </svg>
                    Request
                  </>
                )}
              </button>

              {/* Dropdown for roles when clicking Shortlist */}
              {showRoleMenu && (
                <div
                  ref={roleMenuRef}
                  className="absolute mt-2 bottom-16 left-4 w-48 bg-white shadow-lg rounded-lg z-10"
                >
                  {Array.isArray(roles) ? (
                    roles.map((role, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoleSelect(role, candidateId);
                        }}
                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {role["Role Title"]}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No roles available</div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      newRole();
                    }}
                    className="block w-full px-4 py-2 text-left bg-[#02B491] rounded-b-lg text-sm text-white hover:bg-[#00f2c2] hover:text-black"
                  >
                    + New Role
                  </button>
                </div>
              )}

              {/* Dropdown for roles when clicking Match Request */}
              {showMatchRequestRoleMenu && (
                <div
                  ref={matchRequestRoleMenuRef}
                  className="absolute mt-2 bottom-16 right-4 w-48 bg-white shadow-lg rounded-lg z-10"
                >
                  {Array.isArray(roles) ? (
                    roles.map((role, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMatchRequestRoleSelect(role);
                        }}
                        className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${index === roles.length - 1 ? 'rounded-b-lg' : ''}`}
                      >
                        {role["Role Title"]}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500 rounded-b-lg">No roles available</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      {/* New RoleMatchRequestModal */}
      <RoleMatchRequestModal 
        isOpen={showMatchRequestModal}
        onClose={() => setShowMatchRequestModal(false)}
        candidate={candidate}
        selectedRole={selectedRoleForModal}
        onRequestSent={handleRequestSent}
      />

      {/* Mobile Role Selection Modal for Shortlisting */}
      {showMobileRoleModal && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 md:hidden" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl p-6 mx-4 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Role</h3>
              <button
                onClick={() => setShowMobileRoleModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {Array.isArray(roles) ? (
                roles.map((role, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoleSelect(role, candidateId);
                      setShowMobileRoleModal(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg border border-gray-200 transition-all"
                  >
                    {role["Role Title"]}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">No roles available</div>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  newRole();
                  setShowMobileRoleModal(false);
                }}
                className="w-full px-4 py-3 text-left bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all font-medium"
              >
                + Create New Role
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Role Selection Modal for Match Request */}
      {showMobileRequestModal && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 md:hidden" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl p-6 mx-4 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Role</h3>
              <button
                onClick={() => setShowMobileRequestModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {Array.isArray(roles) ? (
                roles.map((role, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatchRequestRoleSelect(role);
                      setShowMobileRequestModal(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg border border-gray-200 transition-all"
                  >
                    {role["Role Title"]}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">No roles available</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default GeneralistCard;
